import { useContext, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import Storage, { StorageKeys } from 'lane-shared/helpers/Storage';
import {
  RootLevelFlagsContext,
  RootLevelFlags,
} from 'lane-shared/contexts/RootLevelFlagContext/RootLevelFlagsContextProvider';
import { GetAuthTokenResponse } from 'lane-shared/types/auth0/getAuthTokenResponseType';
import { NotFoundError } from 'activate-errors';
import * as Sentry from '@sentry/browser';

// TS-38584: read flags + Auth0 SDK through refs so the function captured by
// `useUserData`'s mount-only effect sees post-mount flag/SDK updates. Without
// this, consolidate=on flips after capture and is never observed.
export function useGetAuthToken(): GetAuthTokenResponse {
  const auth0 = useAuth0();
  const flagsCtx = useContext(RootLevelFlagsContext);

  const auth0Ref = useRef(auth0);
  const flagsRef = useRef<RootLevelFlags>(flagsCtx.flags);

  useEffect(() => {
    auth0Ref.current = auth0;
  }, [auth0]);

  useEffect(() => {
    flagsRef.current = flagsCtx.flags;
  }, [flagsCtx.flags]);

  const getAuthToken: GetAuthTokenResponse = async () => {
    const { getAccessTokenSilently, isLoading, isAuthenticated } =
      auth0Ref.current;
    const {
      useAuth0Enabled: isAuth0Enabled,
      useAuth0AccessToken: useAccessToken,
      useAuth0ConsolidateAuthTokenStorage: useAuth0TokenStorage,
    } = flagsRef.current;

    try {
      if (!isAuth0Enabled || !useAuth0TokenStorage) {
        return await Storage.getItem(StorageKeys.AUTH_TOKEN);
      }

      if (isLoading) {
        return null;
      }

      if (!isAuthenticated) {
        return null;
      }

      if (useAccessToken) {
        return await getAccessTokenSilently({
          timeoutInSeconds: 15,
        });
      }

      const auth0Response = await getAccessTokenSilently({
        detailedResponse: true,
        timeoutInSeconds: 15,
      });

      return auth0Response.id_token;
    } catch (error) {
      if (!(error instanceof NotFoundError)) {
        Sentry.captureException(error);
      }

      return null;
    }
  };

  return getAuthToken;
}
